import {
  Injectable,
  Signal,
  WritableSignal,
  computed,
  inject,
  signal
} from '@angular/core';
import { Store } from '@ngrx/store';
import { take } from 'rxjs';

import { UserPreferencesActions } from '@core/store/user-preferences/user-preferences.actions';
import { StoredColumnConfig } from '@core/store/user-preferences/user-preferences.model';
import { selectTableConfig } from '@core/store/user-preferences/user-preferences.selectors';
import { isNil } from '@core/utils/shared-utils';

import { TableColumn } from '@shared/components/table/table.model';

@Injectable({
  providedIn: 'root'
})
export class TableCustomisationService<T> {
  private readonly store = inject(Store);

  private tableConfigs = new Map<string, WritableSignal<TableColumn<T>[]>>();

  /**
   * The columns signal of a table, created on first use and then kept for good. The
   * identity has to be stable: a consumer that reads a table before it is initialised — or
   * across a re-initialisation — keeps its reactive dependency on this exact signal, so
   * handing out a fresh one would silently orphan it and later width, order and visibility
   * updates would never reach the view.
   *
   * An empty array means "not initialised yet".
   */
  private columnsOf(tableId: string): WritableSignal<TableColumn<T>[]> {
    const existing = this.tableConfigs.get(tableId);
    if (existing) {
      return existing;
    }

    const columns = signal<TableColumn<T>[]>([]);
    this.tableConfigs.set(tableId, columns);
    return columns;
  }

  /** Same as {@link columnsOf}, for the callers that cannot work on an unknown table. */
  private initialisedColumnsOf(tableId: string): WritableSignal<TableColumn<T>[]> {
    const columns = this.columnsOf(tableId);

    if (columns().length === 0) {
      throw new Error(`Table with ID "${tableId}" is not initialized.`);
    }

    return columns;
  }

  /** Synchronous snapshot of the persisted column config for a table. */
  private getTableConfig(tableId: string): StoredColumnConfig[] | null {
    let config: StoredColumnConfig[] | null = null;
    this.store
      .select(selectTableConfig(tableId))
      .pipe(take(1))
      .subscribe((value) => (config = value));
    return config;
  }

  /**
   * Initialises table configuration.
   *
   * @template T
   * @param {string} tableId - Unique identifier for the table.
   * @param {TableColumn<T>[]} columns - Default columns configuration.
   * @returns {Signal<TableColumn<T>[]>} - Signal of the table columns configuration.
   */
  initTableConfig<T>(
    tableId: string,
    columns: TableColumn<T>[]
  ): ReturnType<typeof signal<TableColumn<T>[]>> {
    const savedConfig = this.getTableConfig(tableId);

    const initialColumns = savedConfig
      ? this.mergeWithSavedConfig(columns, savedConfig)
      : this.ensureOrderAndVisibility(columns);

    const columnsSignal = this.columnsOf(tableId) as unknown as WritableSignal<
      TableColumn<T>[]
    >;
    columnsSignal.set(initialColumns);

    return columnsSignal;
  }

  /**
   * Gets visible columns for the specified table ordered by their order property. Returns
   * an empty signal if the table is not initialized yet.
   *
   * @template T
   * @param {string} tableId - Unique identifier for the table.
   * @returns {Signal<TableColumn<T>[]>} - Signal of the visible table columns, or empty
   *   array if not initialized.
   */
  getVisibleColumns<T>(tableId: string): Signal<TableColumn<T>[]> {
    const columnsSignal = this.columnsOf(tableId);

    // @ts-expect-error TypeScript cannot infer the generic type here
    return computed(() => {
      const cols = columnsSignal();
      return cols
        .filter(
          (col) => (col.config?.hideable && col.visible === true) || !col.config?.hideable
        )
        .sort((a, b) => {
          if (a.config?.orderable === undefined && b.config?.orderable === undefined) {
            return 0;
          }
          const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
          const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
          return orderA - orderB;
        });
    });
  }

  /**
   * Gets all columns for the specified table. Returns an empty signal if the table is not
   * initialized yet.
   *
   * @template T
   * @param {string} tableId - Unique identifier for the table.
   * @returns {Signal<TableColumn<T>[]>} - Signal of all table columns, or empty array if
   *   not initialized.
   */
  getAllColumns<T>(tableId: string): Signal<TableColumn<T>[]> {
    const columnsSignal = this.columnsOf(tableId);

    return computed(() => {
      const cols = [...(columnsSignal() as unknown as TableColumn<T>[])];
      return cols.sort((a, b) => {
        if (a.config?.orderable === undefined && b.config?.orderable === undefined) {
          return 0;
        }

        const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
        const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
      });
    });
  }

  /**
   * Updates columns configuration for the specified table.
   *
   * @template T
   * @param {string} tableId - Unique identifier for the table.
   * @param {TableColumn<T>[]} columns - New columns configuration.
   * @throws {Error} - If the table with the specified ID is not initialized.
   */
  updateColumns<T>(tableId: string, columns: TableColumn<T>[]): void {
    const columnsSignal = this.initialisedColumnsOf(tableId);

    // @ts-expect-error TypeScript cannot infer the generic type here
    columnsSignal.set(columns as TableColumn<T>[]);
    this.store.dispatch(
      UserPreferencesActions.setTableConfig({
        tableId,
        config: this.extractStoredConfig(columns)
      })
    );
  }

  /**
   * Toggles the visibility of a column for the specified table.
   *
   * @param {string} tableId - Unique identifier for the table.
   * @param {string} columnKey - Key of the column to toggle visibility.
   * @throws {Error} - If the table with the specified ID is not initialized.
   */
  toggleColumnVisibility(tableId: string, columnKey: string): void {
    const columnsSignal = this.initialisedColumnsOf(tableId);

    const columns = columnsSignal();
    const updatedColumns = columns.map((column) => {
      if (String(column.key) === columnKey && column.config?.hideable) {
        return {
          ...column,
          visible: !column.visible
        };
      }
      return column;
    });

    this.updateColumns(tableId, updatedColumns);
  }

  /**
   * Reorders columns for the specified table.
   *
   * @param {string} tableId - Unique identifier for the table.
   * @param {number} fromIndex - Index of the column to move.
   * @param {number} toIndex - Target index for the column.
   * @throws {Error} - If the table with the specified ID is not initialized.
   */
  reorderColumns(tableId: string, fromIndex: number, toIndex: number): void {
    const columnsSignal = this.initialisedColumnsOf(tableId);

    const columns = [...columnsSignal()].sort((a, b) => {
      const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
      const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    });

    const visibleColumns = columns.filter(
      (col) =>
        col.config?.orderable &&
        ((col.config?.hideable && col.visible === true) || !col.config?.hideable)
    );

    if (
      fromIndex < 0 ||
      fromIndex >= visibleColumns.length ||
      toIndex < 0 ||
      toIndex >= visibleColumns.length
    ) {
      console.warn('Invalid reorder indices');
      return;
    }

    const fromColumn = visibleColumns[fromIndex];
    const toColumn = visibleColumns[toIndex];

    if (!fromColumn.config?.orderable || !toColumn.config?.orderable) {
      console.warn('One of the columns is not orderable. Reordering aborted.');
      return;
    }

    const fromOrder = fromColumn.order ?? 0;
    const toOrder = toColumn.order ?? 0;

    const updatedColumns = columns.map((col) => {
      if (col.key === fromColumn.key) {
        return { ...col, order: toOrder };
      }

      if (fromOrder < toOrder) {
        if (col.order !== undefined && col.order > fromOrder && col.order <= toOrder) {
          return { ...col, order: col.order - 1 };
        }
      } else if (fromOrder > toOrder) {
        if (col.order !== undefined && col.order >= toOrder && col.order < fromOrder) {
          return { ...col, order: col.order + 1 };
        }
      }

      return col;
    });

    const normalizedColumns = this.normalizeOrders(updatedColumns);
    this.updateColumns(tableId, normalizedColumns);
  }

  /**
   * Shows all columns for the specified table.
   *
   * @param {string} tableId - Unique identifier for the table.
   * @throws {Error} - If the table with the specified ID is not initialized.
   */
  showAllColumns(tableId: string): void {
    const columnsSignal = this.initialisedColumnsOf(tableId);

    const columns = columnsSignal();
    const updatedColumns = columns.map((column) => {
      if (column.config?.hideable) {
        return {
          ...column,
          visible: true
        };
      }
      return column;
    });

    this.updateColumns(tableId, updatedColumns);
  }

  /**
   * Hides all columns for the specified table.
   *
   * @param {string} tableId - Unique identifier for the table.
   * @throws {Error} - If the table with the specified ID is not initialized.
   */
  hideAllColumns(tableId: string): void {
    const columnsSignal = this.initialisedColumnsOf(tableId);

    const columns = columnsSignal();
    const updatedColumns = columns.map((column) => {
      if (column.config?.hideable) {
        return {
          ...column,
          visible: false
        };
      }
      return column;
    });

    this.updateColumns(tableId, updatedColumns);
  }

  /**
   * Resets columns to default configuration for the specified table.
   *
   * @template T
   * @param {string} tableId - Unique identifier for the table.
   * @param {TableColumn<T>[]} defaultColumns - Default columns configuration.
   */
  resetToDefault<T>(tableId: string, defaultColumns: TableColumn<T>[]): void {
    const resetColumns = defaultColumns.map((col, index) => ({
      ...col,
      visible: col.config?.hideable ? true : col.visible,
      order: index,
      width: col.width
    }));

    this.updateColumns(tableId, resetColumns);
  }

  /**
   * Clears stored configuration for the specified table.
   *
   * @param {string} tableId - Unique identifier for the table.
   */
  clearTableConfig(tableId: string): void {
    this.store.dispatch(UserPreferencesActions.removeTableConfig({ tableId }));
    // Emptied rather than dropped, so a table still on screen is notified and falls back
    // to its default columns.
    this.columnsOf(tableId).set([]);
  }

  /**
   * Exports the current configuration of the specified table as a JSON string.
   *
   * @param {string} tableId - Unique identifier for the table.
   * @returns {string} - JSON string of the table configuration.
   * @throws {Error} - If the table with the specified ID is not initialized.
   */
  exportConfig(tableId: string): string {
    const columnsSignal = this.initialisedColumnsOf(tableId);

    const config = this.extractStoredConfig(columnsSignal());
    return JSON.stringify(config, null, 2);
  }

  /**
   * Imports table configuration from a JSON string.
   *
   * @param {string} tableId - Unique identifier for the table.
   * @param {string} configJson - JSON string of the table configuration.
   * @throws {Error} - If the configuration format is invalid.
   */
  importConfig(tableId: string, configJson: string): void {
    try {
      const config: StoredColumnConfig[] = JSON.parse(configJson);
      this.store.dispatch(UserPreferencesActions.setTableConfig({ tableId, config }));

      const columnsSignal = this.columnsOf(tableId);
      if (columnsSignal().length > 0) {
        const mergedColumns = this.mergeWithSavedConfig(columnsSignal(), config);
        columnsSignal.set(mergedColumns);
      }
    } catch (error) {
      console.error('Failed to import table configuration:', error);
      throw new Error('Invalid configuration format', { cause: error });
    }
  }

  /**
   * Checks if there is a stored configuration for the specified table.
   *
   * @param {string} tableId - Unique identifier for the table.
   * @returns {boolean} - True if a stored configuration exists, false otherwise.
   */
  hasStoredConfig(tableId: string): boolean {
    return this.getTableConfig(tableId) !== null;
  }

  /**
   * Checks if a table has been initialized in memory.
   *
   * @param {string} tableId - Unique identifier for the table.
   * @returns {boolean} - True if the table is initialized, false otherwise.
   */
  isTableInitialized(tableId: string): boolean {
    return (this.tableConfigs.get(tableId)?.().length ?? 0) > 0;
  }

  /**
   * Updates the width of a column in the specified table.
   *
   * @param {string} tableId - Unique identifier for the table
   * @param {string} columnKey - Key of the column to update
   * @param {number} width - New width for the column
   * @throws {Error} - If the table with the specified ID is not initialized.
   */
  updateColumnWidth(tableId: string, columnKey: string, width: number): void {
    const columnsSignal = this.initialisedColumnsOf(tableId);

    const columns = columnsSignal();
    const updatedColumns = columns.map((col) => {
      if (String(col.key) === columnKey && col.config?.resizeable) {
        return { ...col, width };
      }
      return col;
    });

    this.updateColumns(tableId, updatedColumns);
  }

  /**
   * Updates the reference display mode of a column in the specified table.
   *
   * @param {string} tableId - Unique identifier for the table.
   * @param {string} columnKey - Key of the column to update.
   * @param {'uid' | 'name'} displayMode - New display mode for the column.
   * @throws {Error} - If the table with the specified ID is not initialized.
   */
  updateColumnDisplayMode(
    tableId: string,
    columnKey: string,
    displayMode: 'uid' | 'name'
  ): void {
    const columnsSignal = this.initialisedColumnsOf(tableId);
    const columns = columnsSignal();
    const updatedColumns = columns.map((col) =>
      String(col.key) === columnKey ? { ...col, displayMode } : col
    );
    columnsSignal.set(updatedColumns);
    this.store.dispatch(
      UserPreferencesActions.setColumnDisplayMode({ tableId, columnKey, displayMode })
    );
  }

  /**
   * Moves a column to the left in the specified table.
   *
   * @param {string} tableId - Unique identifier for the table.
   * @param {string} columnKey - Key of the column to move.
   * @throws {Error} - If the table with the specified ID is not initialized.
   */
  moveColumnLeft(tableId: string, columnKey: string): void {
    const { currentIndex, columns } = this.moveColumns(tableId, columnKey);

    if (currentIndex > 0 && columns[currentIndex]?.config?.orderable) {
      this.reorderColumns(tableId, currentIndex, currentIndex - 1);
    }
  }

  /**
   * Moves a column to the right in the specified table.
   *
   * @param {string} tableId - Unique identifier for the table.
   * @param {string} columnKey - Key of the column to move.
   * @throws {Error} - If the table with the specified ID is not initialized.
   */
  moveColumnRight(tableId: string, columnKey: string): void {
    const { currentIndex, columns } = this.moveColumns(tableId, columnKey);

    if (currentIndex < columns.length - 1 && columns[currentIndex]?.config?.orderable) {
      this.reorderColumns(tableId, currentIndex, currentIndex + 1);
    }
  }

  private normalizeOrders<T>(columns: TableColumn<T>[]): TableColumn<T>[] {
    const sorted = [...columns].sort((a, b) => {
      const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
      const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    });

    return sorted.map((col, index) => ({
      ...col,
      order: index
    }));
  }

  private moveColumns(
    tableId: string,
    columnKey: string
  ): { currentIndex: number; columns: TableColumn<T>[] } {
    const columnsSignal = this.initialisedColumnsOf(tableId);

    const allColumns = [...columnsSignal()].sort((a, b) => {
      const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
      const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    });

    const visibleColumns = allColumns.filter(
      (col) =>
        col.config?.orderable &&
        ((col.config?.hideable && col.visible) || !col.config?.hideable)
    );

    const currentIndex = visibleColumns.findIndex((col) => String(col.key) === columnKey);

    return { currentIndex, columns: visibleColumns };
  }

  private extractStoredConfig<T>(columns: TableColumn<T>[]): StoredColumnConfig[] {
    return columns
      .filter(
        (col) =>
          col.config?.hideable ||
          col.config?.orderable ||
          col.config?.resizeable ||
          col.displayMode !== undefined
      )
      .map((col) => ({
        key: String(col.key),
        visible: col.visible ?? true,
        order: col.order ?? 0,
        width: isNil(col.width) ? undefined : col.width,
        ...(col.displayMode !== undefined ? { displayMode: col.displayMode } : {})
      }));
  }

  private mergeWithSavedConfig<T>(
    defaultColumns: TableColumn<T>[],
    savedConfig: StoredColumnConfig[]
  ): TableColumn<T>[] {
    const savedMap = new Map(savedConfig.map((col) => [col.key, col]));

    return defaultColumns
      .map((col) => {
        const saved = savedMap.get(String(col.key));
        return {
          ...col,
          visible: saved?.visible ?? col.visible,
          width: saved?.width ?? col.width,
          // A column the user cannot move still gets an order saved, as a snapshot of the
          // defaults at the time. Honouring it would interleave the column with the ones
          // added since, whose default positions overlap the stale ones.
          order: col.config?.orderable ? (saved?.order ?? col.order) : col.order,
          displayMode: saved?.displayMode ?? col.displayMode
        };
      })
      .sort((a, b) => {
        const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
        const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
      });
  }

  private ensureOrderAndVisibility<T>(columns: TableColumn<T>[]): TableColumn<T>[] {
    return columns.map((col, index) => ({
      ...col,
      order: col.order ?? index,
      visible: col.config?.hideable ? (col.visible ?? true) : col.visible,
      width: col.config?.resizeable ? (col.width ?? 90) : col.width
    }));
  }
}
